import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
  Alert,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { API_URL } from "../../common/Constants";
import AuthLayout from "../auth/AuthLayout";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const InviteSetup = () => {
  const query = useQuery();
  const token = query.get("token");
  const navigate = useNavigate();

  const [valid, setValid] = useState(null); // null=verifying, true/false after check
  const [expired, setExpired] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setValid(false);
      setError("Missing invite token.");
      return;
    }
    axios
      .get(`${API_URL}/users/verify-invite?token=${token}`)
      .then((res) => {
        setValid(Boolean(res.data.valid));
      })
      .catch((err) => {
        if (err?.response?.status === 410) {
          setExpired(true);
          setError("Invite link expired. Contact the account owner to resend.");
        } else {
          setValid(false);
          setError("Invalid invite link.");
        }
      });
  }, [token]);

  const passwordStrength = (pwd) => {
    let score = 0;
    if (!pwd) return 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return Math.min(score, 5);
  };
  const strength = useMemo(() => passwordStrength(password), [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setTouched(true);
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/users/setup-password`, {
        token,
        password,
      });
      if (res?.data?.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/Login?setup=success");
        }, 2000); // 2 seconds delay
      } else {
        setError("Could not set password. " + (res?.data?.reason || ""));
      }
    } catch (err) {
      if (err?.response?.status === 410) {
        setExpired(true);
        setError("Invite link expired. Contact the account owner to resend.");
      } else {
        setError("Could not set password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state while verifying token
  if (valid === null) {
    return (
      <AuthLayout>
        <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  if (expired || valid === false) {
    return (
      <AuthLayout>
        <Alert severity="error" sx={{ my: 2 }}>
          {expired
            ? "Invite link expired. Contact the account owner to resend."
            : error || "Invalid or expired invite link."}
        </Alert>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <Alert severity="success" sx={{ my: 2 }}>
          Password updated successfully! Redirecting to login…
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set your password">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <LinearProgress variant="determinate" value={(strength / 5) * 100} />
            <Typography variant="caption" color="text.secondary">
              Password strength
            </Typography>
          </Box>
          <TextField
            fullWidth
            type={showConfirm ? "text" : "password"}
            label="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            error={touched && confirmPassword.length > 0 && confirmPassword !== password}
            helperText={
              touched && confirmPassword.length > 0 && confirmPassword !== password
                ? "Passwords do not match"
                : " "
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm((s) => !s)} edge="end">
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" fullWidth variant="contained" disabled={submitting}>
            {submitting ? "Setting password…" : "Set Password"}
          </Button>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default InviteSetup;
